/**
 * CostCalculator Component - REBUILT
 * 
 * Main component for the MIPS Cost Category dashboard.
 * Matches the comprehensive Sample_Cost_Dashboard.jsx design.
 */

import { useState, useEffect, useRef } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Users, 
  Activity, 
  Target, 
  DollarSign,
  AlertCircle,
  LayoutDashboard,
} from 'lucide-react';
import { CostModeledBanner } from './CostModeledBanner';
import { CostMeasureDetailPanel } from './CostMeasureDetailPanel';
import { TPCCMeasureDetailPanel } from './TPCCMeasureDetailPanel';
import { MSPBMeasureDetailPanel } from './MSPBMeasureDetailPanel';
import { CostStrategiesTab } from './CostStrategiesTab';
import { CostPatientsTab } from './CostPatientsTab';
import { CostSummaryPage } from './CostSummaryPage';
import {
  getCostDashboardData,
  type CostDashboardData,
  type CostMeasure,
} from '@/lib/cost-calculator-utils';
import { type Provider } from '@/lib/measure-outcomes';
import { useProgramYear } from '@/contexts/ProgramYearContext';

interface CostCalculatorProps {
  practiceId: string;
  practiceName?: string;
  useModeledCost: boolean;
  modeledCostPercent: number;
  costWeight: number;
  onToggleModeled: (useModeled: boolean) => void;
  /** Callback with raw percentage score (0-100). Parent applies weights. */
  onScoreChange?: (rawScore: number) => void;
  providers?: Provider[];
  evaluationType: 'GROUP' | 'NPI';
  selectedNpi?: string;
  onEvaluationTypeChange?: (type: 'GROUP' | 'NPI') => void;
  onNpiChange?: (npi: string) => void;
}

type TabId = 'summary' | 'strategies' | 'patients';

export function CostCalculator({
  practiceId,
  useModeledCost,
  modeledCostPercent,
  costWeight,
  onToggleModeled,
  onScoreChange,
  providers: externalProviders,
  evaluationType,
  selectedNpi,
}: CostCalculatorProps) {
  // Viewing year — cost data must be year-scoped (fix: previously omitted, so
  // the backend silently fell back to the operating year regardless of the
  // year selector; see multi-year-and-locking.mdc)
  const { programYear } = useProgramYear();

  // State
  const [dashboardData, setDashboardData] = useState<CostDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [providers, setProviders] = useState<Provider[]>(externalProviders || []);
  const [activeTab, setActiveTab] = useState<TabId>('summary');
  const [selectedMeasure, setSelectedMeasure] = useState<CostMeasure | null>(null);
  const [initialStrategyId, setInitialStrategyId] = useState<string | null>(null);
  const [initialHighCostFilter, setInitialHighCostFilter] = useState<boolean>(false);
  const [cameFromSummary, setCameFromSummary] = useState<boolean>(false);
  
  // Refs
  const hasLoadedRef = useRef(false);
  const onScoreChangeRef = useRef(onScoreChange);
  
  useEffect(() => {
    onScoreChangeRef.current = onScoreChange;
  }, [onScoreChange]);
  
  useEffect(() => {
    if (externalProviders) {
      setProviders(externalProviders);
    }
  }, [externalProviders]);

  // Load dashboard data
  useEffect(() => {
    const loadData = async () => {
      if (!hasLoadedRef.current) {
        setIsLoading(true);
      }
      setError(null);
      
      try {
        const npi = evaluationType === 'NPI' ? selectedNpi : undefined;
        const data = await getCostDashboardData(practiceId, { npi, year: programYear });
        
        if (data) {
          setDashboardData(data);
          
          // Notify parent of raw score - parent applies weights
          if (!useModeledCost && onScoreChangeRef.current) {
            onScoreChangeRef.current(data.summary.categoryScore);
          }
        } else {
          setError('No cost data available. Please upload cost feedback reports in the Configuration tab.');
        }
      } catch (err) {
        console.error('Error loading cost dashboard:', err);
        setError('Failed to load cost dashboard data.');
      } finally {
        setIsLoading(false);
        hasLoadedRef.current = true;
      }
    };
    
    loadData();
  }, [practiceId, evaluationType, selectedNpi, useModeledCost, programYear]);

  // Handle modeled toggle
  const handleToggleModeled = (useModeled: boolean) => {
    onToggleModeled(useModeled);
    
    // Notify parent of raw score - parent applies weights
    if (useModeled) {
      if (onScoreChangeRef.current) {
        onScoreChangeRef.current(modeledCostPercent);
      }
    } else if (dashboardData) {
      if (onScoreChangeRef.current) {
        onScoreChangeRef.current(dashboardData.summary.categoryScore);
      }
    }
  };

  // Derived data
  const summary = dashboardData?.summary;
  const measures = dashboardData?.measures ?? [];
  const scoredMeasures = measures.filter(m => m.scored);
  const unscoredMeasures = measures.filter(m => !m.scored);
  
  // Tab configuration (removed Measures and Trends tabs per user request)
  const tabs: { id: TabId; label: string; icon: typeof Activity }[] = [
    { id: 'summary', label: 'Summary', icon: LayoutDashboard },
    { id: 'strategies', label: 'Care Interventions', icon: Target },
    { id: 'patients', label: 'Patients', icon: Users },
  ];

  // Loading skeleton
  if (isLoading && !hasLoadedRef.current) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Error state (no data)
  if (!dashboardData && error) {
    return (
      <div className="space-y-6">
        <CostModeledBanner
          useModeled={useModeledCost}
          modeledPercent={modeledCostPercent}
          calculatedPercent={0}
          costWeight={costWeight}
          onToggle={handleToggleModeled}
        />
        
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        
        <div className="border-2 border-dashed border-muted-foreground/25 rounded-xl py-16 text-center">
          <DollarSign className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Cost Data Available</h3>
          <p className="text-muted-foreground mb-4 max-w-md mx-auto">
            Upload your QPP Cost Feedback Reports in the Configuration → QPP Reports tab 
            to view your Cost Category performance dashboard.
          </p>
          <p className="text-sm text-muted-foreground">
            Cost measures include TPCC, MSPB, and Episode-Based Measures
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Modeled banner */}
      <CostModeledBanner
        useModeled={useModeledCost}
        modeledPercent={modeledCostPercent}
        calculatedPercent={summary?.categoryScore ?? 0}
        costWeight={costWeight}
        onToggle={handleToggleModeled}
      />

      {/* When modeled, show only the disabled message */}
      {useModeledCost ? (
        <Alert className="bg-muted/50">
          <AlertDescription>
            The Cost Calculator is disabled while using modeled scores. 
            Switch to the calculator to view cost performance data.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          {/* Navigation Tabs */}
          <div className="border-b border-border">
            <div className="flex gap-1">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setSelectedMeasure(null); }}
                    className={`flex items-center gap-2 px-5 py-3 font-medium text-sm border-b-2 transition-colors ${
                      activeTab === tab.id 
                        ? 'border-primary text-primary' 
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content - min-height to prevent layout jumps */}
          {/* Tabs kept mounted with display:none to prevent remounting */}
          <div className="min-h-[600px]">
            {/* Summary Tab - both views kept mounted, toggled with display for smooth transitions */}
            <div style={{ display: activeTab === 'summary' ? 'block' : 'none' }}>
              {/* Measure Detail Panel - shown when measure selected */}
              {/* Conditionally render TPCC, MSPB, or generic panel based on measureId */}
              <div style={{ display: selectedMeasure ? 'block' : 'none' }}>
                {selectedMeasure && selectedMeasure.measureId === 'TPCC_1' && (
                  <TPCCMeasureDetailPanel
                    measure={selectedMeasure}
                    practiceId={practiceId}
                    npi={evaluationType === 'NPI' ? selectedNpi : undefined}
                    onClose={() => setSelectedMeasure(null)}
                  />
                )}
                {selectedMeasure && selectedMeasure.measureId === 'MSPB_1' && (
                  <MSPBMeasureDetailPanel
                    measure={selectedMeasure}
                    practiceId={practiceId}
                    npi={evaluationType === 'NPI' ? selectedNpi : undefined}
                    onClose={() => setSelectedMeasure(null)}
                  />
                )}
                {selectedMeasure && selectedMeasure.measureId !== 'TPCC_1' && selectedMeasure.measureId !== 'MSPB_1' && (
                  <CostMeasureDetailPanel
                    measure={selectedMeasure}
                    practiceId={practiceId}
                    npi={evaluationType === 'NPI' ? selectedNpi : undefined}
                    onClose={() => setSelectedMeasure(null)}
                  />
                )}
              </div>
              {/* Summary Page - shown when no measure selected */}
              <div style={{ display: !selectedMeasure ? 'block' : 'none' }}>
                <CostSummaryPage
                  practiceId={practiceId}
                  evaluationType={evaluationType}
                  selectedNpi={selectedNpi}
                  onNavigateToPatients={() => {
                    setActiveTab('patients');
                  }}
                  onNavigateToStrategy={(strategyId, highCostFilter) => {
                    setInitialStrategyId(strategyId);
                    setInitialHighCostFilter(highCostFilter || false);
                    setCameFromSummary(true);
                    setActiveTab('strategies');
                  }}
                  onNavigateToMeasure={(measureId) => {
                    const measure = measures.find(m => m.measureId === measureId);
                    if (measure) {
                      setSelectedMeasure(measure);
                    }
                  }}
                />
              </div>
            </div>

            <div style={{ display: activeTab === 'strategies' ? 'block' : 'none' }}>
              <CostStrategiesTab
                practiceId={practiceId}
                npi={evaluationType === 'NPI' ? selectedNpi : undefined}
                initialStrategyId={initialStrategyId}
                initialHighCostFilter={initialHighCostFilter}
                cameFromSummary={cameFromSummary}
                onStrategyIdConsumed={() => { setInitialStrategyId(null); setInitialHighCostFilter(false); }}
                onBackToSummary={() => { setCameFromSummary(false); setActiveTab('summary'); }}
              />
            </div>

            <div style={{ display: activeTab === 'patients' ? 'block' : 'none' }}>
              <CostPatientsTab
                practiceId={practiceId}
                npi={evaluationType === 'NPI' ? selectedNpi : undefined}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
